import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { createOrder } from "../lib/api";
import { toast } from "sonner";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, clear } =
    useCart();
  const freeShipThreshold = 50;
  const progress = Math.min(100, (subtotal / freeShipThreshold) * 100);
  const remaining = Math.max(0, freeShipThreshold - subtotal);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-[#FFF2EC] shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#1A1410]/10">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            <h3 className="font-serif-display text-xl font-semibold">
              Your Cart
            </h3>
          </div>
          <button onClick={() => setIsOpen(false)} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free shipping progress */}
        <div className="p-5 bg-[#FFE4D6] border-b border-[#1A1410]/10">
          <div className="text-xs font-medium text-[#1A1410]/80 mb-2">
            {remaining > 0
              ? `You're $${remaining.toFixed(2)} away from Free Shipping!`
              : "You unlocked Free Shipping!"}
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden">
            <div
              className="h-full bg-[#DD4D2A] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <ShoppingBag className="w-10 h-10 text-[#1A1410]/30 mb-3" />
              <p className="font-serif-display text-lg font-semibold">
                Your cart is empty
              </p>
              <p className="text-sm text-[#1A1410]/60 mt-1">
                Add a rebounder to get started
              </p>
              <button
                onClick={() => setIsOpen(false)}
                className="mt-5 btn-coral px-6 py-3 rounded-full text-sm uppercase"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            items.map((i) => (
              <div
                key={i.id}
                className="flex gap-3 bg-white rounded-2xl p-3 border border-[#1A1410]/10"
              >
                <img
                  src={i.image}
                  alt={i.name}
                  className="w-20 h-20 object-cover rounded-xl"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm line-clamp-2">
                    {i.name}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-semibold">${i.price}.00</span>
                    {i.compareAt && (
                      <span className="text-xs text-[#1A1410]/40 line-through">
                        ${i.compareAt}.00
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center border border-[#1A1410]/15 rounded-full">
                      <button
                        onClick={() => updateQty(i.id, i.qty - 1)}
                        className="p-1.5 hover:bg-[#1A1410]/5 rounded-l-full"
                        aria-label="Decrease"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-3 text-sm font-medium">{i.qty}</span>
                      <button
                        onClick={() => updateQty(i.id, i.qty + 1)}
                        className="p-1.5 hover:bg-[#1A1410]/5 rounded-r-full"
                        aria-label="Increase"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        removeItem(i.id);
                        toast("Removed from cart");
                      }}
                      className="text-[#1A1410]/50 hover:text-[#DD4D2A] p-1"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="p-5 border-t border-[#1A1410]/10 bg-white">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-[#1A1410]/70">Subtotal</span>
              <span className="font-serif-display text-xl font-semibold">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-[#1A1410]/50 mb-4">
              Taxes, discounts and shipping calculated at checkout.
            </p>
            <button
                onClick={async () => {
                  try {
                    const order = await createOrder(items);
                    toast.success(
                      `Order ${order.id.slice(0, 8)} confirmed. Thank you!`
                    );
                    clear();
                    setIsOpen(false);
                  } catch {
                    toast.error("Could not place order. Try again.");
                  }
                }}
                className="w-full btn-coral py-4 rounded-full text-sm uppercase"
            >
              Secure Checkout
            </button>
            <p className="mt-2 text-[10px] text-center uppercase tracking-wider text-[#1A1410]/50">
              256-bit SSL encrypted checkout
            </p>
            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className="mt-3 block text-center text-xs uppercase tracking-wider text-[#1A1410]/60 hover:text-[#DD4D2A]"
            >
              View full cart
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
